import { useCallback, useEffect, useMemo, useState } from "react";
import { descriptionByFactCode } from "system/libraries/constants";
import { useParams } from "react-router-dom";
import {
  ComplianceStatusToString,
  camelPad,
  getFactValue,
  isFactUnknown,
  normalizeString,
  statusToNumber,
  toDate,
  getSorter,
  toFormatedDate,
} from "system/libraries/utilities";
import { ListResponse, MachineAtTime } from "system/types/wireTypes";
import useMultiFilters from "system/hooks/use-multi-filters";
import usePageTitle from "system/hooks/use-page-title";
import MachineFact from "system/types/interfaces/MachineFact";
import ComplianceStatus from "components/ui/ComplianceStatus";
import usePageNavigation from "system/hooks/use-page-navigation";
import AppLink from "components/ui/AppLink";
import useApi from "system/hooks/use-api";
import LoadingPage from "./system/LoadingPage";
import useBreadcrumbs from "system/hooks/use-breadcrumbs";
import IAppBreadcrumb from "system/types/interfaces/IAppBreadcrumb";
import ActionButtons from "components/ui/ActionButtons";
import ExportToCsvButton from "components/ui/ExportToCsvButton";
import EndpointFilter from "components/ui/EndpointFilter";
import SortableTableHeader from "components/ui/SortableTableHeader";
import EndpointsDataGrid from "components/ui/EndpointsDataGrid";
import { GridColDef } from "@mui/x-data-grid";
import useSearchField from "system/hooks/use-search-field";
import { RetryErrorPage } from "./system";

const getSorterFacts = (field: string, descending: boolean = false) => {
  return function (a: MachineAtTime, b: MachineAtTime): number {
    const valueA: any = getFactValue(a.facts as MachineFact[], field);
    const valueB: any = getFactValue(b.facts as MachineFact[], field);

    const leftValue = isNaN(valueA) ? valueA.toString().toLowerCase() : valueA;
    const rightValue = isNaN(valueB) ? valueB.toString().toLowerCase() : valueB;

    if (leftValue < rightValue || (leftValue === null && rightValue !== null)) {
      return descending ? 1 : -1;
    }

    if (leftValue > rightValue || (leftValue !== null && rightValue === null)) {
      return descending ? -1 : 1;
    }

    return 0;
  };
};

const EndpointsByStatusPage = () => {
  const api = useApi();
  const { navigateToHomePage, navigateToEndpointPage , getHomePageUrl } = usePageNavigation();
  const { statusString, time } = useParams();
  const { sortDesc, sortField } = useSearchField();
  const complianceStatus = statusToNumber(statusString ?? "");
  const { machineFilterSet } = useMultiFilters();
  const [formattedTime] = useState<string>(toFormatedDate(time ?? ""));
  const [data, setData] = useState<MachineAtTime[] | null>(null);
  const [columnCodes] = useState<string[]>(["CONFIG", "COMPLIANCESTATUS", "COMPLIANCEJUSTIFICATION", "REPO", "ADSITE"]);
  const [error, setError] = useState<string | null>(null);

  const refreshHandler = () => {
    setData(null);
    setError(null);
    loadMachineByStatusData();
  };

  const statusText = useMemo(() => {
    var result = camelPad(statusString ?? "");
    if (result === "Failed" || result === "Critical") {
      return "Critical action required";
    }

    return result;
  }, [statusString]);

  usePageTitle({
    title: statusText,
    subTitle: `Endpoints by state`,
  });

  useBreadcrumbs(
    useMemo<IAppBreadcrumb[]>(() => {
      let result: IAppBreadcrumb[] = [];

      if (time) {
        result.push({
          title: "Time",
          name: toFormatedDate(time),
          url: getHomePageUrl(time),
        });
      }

      if (statusString && statusString !== "all") {
        result.push({
          title: "State",
          name: normalizeString(statusText),
        });
      }

      return result;
    }, [time, statusString, statusText])
  );

  const columns = useMemo<GridColDef[]>(() => {
    // Headers stay the original component so sorting keeps writing sort/desc to the url.
    const header = (title: string, property: string) => () => (
      <SortableTableHeader title={title} property={property} className="ts-date-title truncated-header" />
    );
    const base = { sortable: false, disableColumnMenu: true, resizable: false };
    const facts = (row: MachineAtTime) => row.facts?.map((f) => f as MachineFact);
    // Widths cover the worst case per column; a grid cannot grow with its content.
    const widthByCode: { [code: string]: number } = {
      CONFIG: 170,
      COMPLIANCESTATUS: 255,
      COMPLIANCEJUSTIFICATION: 320,
      REPO: 210,
      ADSITE: 150,
    };

    return [
      {
        ...base,
        field: "endpoint",
        width: 180,
        renderHeader: header("Endpoint", "endpoint"),
        renderCell: (params) => (
          <AppLink onClick={() => navigateToEndpointPage(params.row.machineId, time)}>
            {getFactValue(facts(params.row), "COMPUTERNAME")}
          </AppLink>
        ),
      },
      {
        ...base,
        field: "time",
        width: 178,
        renderHeader: header("Assessment changed", "time"),
        valueGetter: (_v, row) => toFormatedDate(row.referenceJsTime!),
      },
      {
        ...base,
        field: "lastrecorded",
        width: 150,
        renderHeader: header("Last recorded", "lastrecorded"),
        valueGetter: (_v, row) => toFormatedDate(row.lastReportedJsTime!),
      },
      ...columnCodes.map((code) => {
        const description = descriptionByFactCode[code];
        const column: GridColDef = {
          ...base,
          field: description,
          width: widthByCode[code] ?? 160,
          renderHeader: header(description, description),
        };

        if (code === "COMPLIANCESTATUS") {
          column.renderCell = (params) => (
            <ComplianceStatus code={+(getFactValue(facts(params.row), code) ?? 0)} isUnknown={isFactUnknown(facts(params.row), code)} />
          );
        } else {
          column.valueGetter = (_v, row) => getFactValue(facts(row), code);
          if (code === "COMPLIANCEJUSTIFICATION") {
            column.flex = 1;
            column.minWidth = 240;
          }
        }

        return column;
      }),
    ];
  }, [columnCodes, navigateToEndpointPage, time]);

  const loadMachineByStatusData = useCallback(() => {
    if (!time || complianceStatus === null) {
      return;
    }

    const controller = new AbortController();
    const fetchData = async () => {
      try {
        const data = await api.list.machinesByStatus({
          complianceStatus,
          fromTime: new Date(time),
          filters: machineFilterSet,
          isUnknown: complianceStatus === 0,
          noLimit: false,
        }, {
          retries: 3,
          baseDelay: 1000,
          maxDelay: 10000,
          signal: controller.signal,
        });
        if (data && data.isSuccess) {
          setData(data.items);
          setError(null);
        } else {
          setError(data?.failMessage || "Failed to load endpoint status data after multiple attempts.");
        }
      } catch (error: any) {
        if (error.name === "AbortError") return;
        console.error("Error fetching endpoint status data:", error);
        setError(error.message || "Failed to load endpoint status data after multiple attempts.");
      }
    };

    fetchData();

    return () => {
      controller.abort();
      setData(null);
      setError(null);
    };
  }, [setData, api, time, complianceStatus, machineFilterSet]);

  useEffect(() => loadMachineByStatusData(), [loadMachineByStatusData]);

  const getMachineByStatusDataForExport = useCallback(
    (noLimit?: boolean) => {
      if (!time || complianceStatus === null) {
        return;
      }

      return api.list.machinesByStatus({
        complianceStatus,
        fromTime: new Date(time),
        filters: machineFilterSet,
        isUnknown: complianceStatus === 0,
        noLimit: true,
      });
    },
    [api, time, complianceStatus, machineFilterSet]
  );

  const exportDataHandler = useCallback(async () => {
    const csvDataToExport = await getMachineByStatusDataForExport(true)
      ?.then((data: ListResponse<MachineAtTime> | null) => {
        return data?.isSuccess ? data.items : null;
      })
      .catch(() => null);

    return csvDataToExport?.map((item) => ({
      Endpoint: getFactValue(
        item.facts?.map((f) => f as MachineFact),
        "COMPUTERNAME"
      ),
      AssessmentTime: toFormatedDate(item.referenceJsTime!),
      AssessmentTimeISO8601: item.referenceJsTime,
      LastRecorded: toFormatedDate(item.lastReportedJsTime!),
      LastRecordedISO8601: item.lastReportedJsTime,
      Configuration: getFactValue(
        item.facts?.map((f) => f as MachineFact),
        "CONFIG"
      ),
      State: ComplianceStatusToString(
        getFactValue(
          item.facts?.map((f) => f as MachineFact),
          "COMPLIANCESTATUS"
        )
      ),
      Justification: getFactValue(
        item.facts?.map((f) => f as MachineFact),
        "COMPLIANCEJUSTIFICATION"
      ),
      Repository: getFactValue(
        item.facts?.map((f) => f as MachineFact),
        "REPO"
      ),
      ADSite: getFactValue(
        item.facts?.map((f) => f as MachineFact),
        "ADSITE"
      ),
    }));
  }, [getMachineByStatusDataForExport]);

  const sortedData = useMemo<typeof data>(() => {
    if (data === null) {
      return null;
    }

    switch (sortField) {
      case "time":
        return [...data].sort(getSorter("referenceJsTime", sortDesc));
      case "lastrecorded":
        return [...data].sort(getSorter("lastReportedJsTime", sortDesc));
      case "endpoint":
        return [...data].sort(getSorterFacts("COMPUTERNAME", sortDesc));
      case "Configuration":
        return [...data].sort(getSorterFacts("CONFIG", sortDesc));
      case "State":
        return [...data].sort(getSorterFacts("COMPLIANCESTATUS", sortDesc));
      case "Justification":
        return [...data].sort(getSorterFacts("COMPLIANCEJUSTIFICATION", sortDesc));
      case "Repository":
        return [...data].sort(getSorterFacts("REPO", sortDesc));
      case "Site":
        return [...data].sort(getSorterFacts("ADSITE", sortDesc));
      default:
        return [...data];
    }
  }, [data, sortField, sortDesc]);

  const exportData = useMemo(
    () => ({
      fileName: `Aiden ${statusText} Endpoints by State ${toDate(time)}`,
      source: exportDataHandler,
    }),
    [exportDataHandler, time, statusText]
  );

  if (!formattedTime || complianceStatus === null) {
    navigateToHomePage();
    return <></>;
  } 

  if (error) {
    return (
      <RetryErrorPage errorMessage="We could not retrieve the information for this page." refreshHandler={refreshHandler} />
    );
  }

  return sortedData !== null ? (
    <>
      <ActionButtons onRefresh={refreshHandler}>
        <ExportToCsvButton items={[exportData]} />
      </ActionButtons>
      <EndpointFilter />
      <EndpointsDataGrid rows={sortedData} columns={columns} />
    </>
  ) : (
    <LoadingPage />
  );
};

export default EndpointsByStatusPage;
